// Main Visit Wizard Component - Multi-step workflow for creating visits
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { VisitWizardProvider, useVisitWizard } from '../../../contexts/VisitWizardContext'
import { Card, CardContent } from '../../../components/ui/card'
import { Progress } from '../../../components/ui/progress'
import { Button } from '../../../components/ui/button'
import { ArrowLeft, X } from 'lucide-react'
import { STEP_LABELS, WizardStep } from '../../../types/visit-wizard'

// Import step components
import { ClientSelection } from './steps/ClientSelection'
import { ClientReview } from './steps/ClientReview'
import { Classification } from './steps/Classification'
import { Denial } from './steps/Denial'
import { AssistanceSelection } from './steps/AssistanceSelection'
import { AssistanceForm } from './steps/AssistanceForm'
import { VisitSummary } from './steps/VisitSummary'

function WizardContent() {
  const { state, goBack } = useVisitWizard()
  const navigate = useNavigate()

  // Render appropriate step component
  const renderStep = () => {
    switch (state.currentStep) {
      case 'client-selection':
        return <ClientSelection />
      case 'client-review':
        return <ClientReview />
      case 'classification':
        return <Classification />
      case 'denial':
        return <Denial />
      case 'assistance-selection':
        return <AssistanceSelection />
      case 'assistance-form':
        return <AssistanceForm />
      case 'summary':
        return <VisitSummary />
      default:
        return <div>Unknown step</div>
    }
  }

  // Calculate step number for progress
  const getStepNumber = (): number => {
    const stepMap: Record<WizardStep, number> = {
      'client-selection': 1,
      'client-review': 2,
      'classification': 3,
      'denial': 4,
      'assistance-selection': 4,
      'assistance-form': 5,
      'summary': 6,
      'confirmation': 7,
    }
    return stepMap[state.currentStep] || 0
  }

  // Calculate total steps based on status (101 = denied, else assisted)
  const isDenied = state.classification?.decision === 'deny'
  const totalSteps = isDenied ? 4 : 7
  const currentStepNumber = getStepNumber()
  const progress = (currentStepNumber / totalSteps) * 100

  // Show back button for steps after client selection
  const showBackButton = state.currentStep !== 'client-selection'

  // Warn before leaving if work in progress
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state.visitId && state.currentStep !== 'confirmation') {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [state])

  const handleCancel = () => {
    if (state.visitId || state.selectedClient) {
      const confirmed = window.confirm(
        'Are you sure you want to cancel? All unsaved progress will be lost.'
      )
      if (!confirmed) return
    }
    navigate('/visits')
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6">
      <div className="max-w-5xl mx-auto">
        <Card>
          <CardContent className="p-6">
            {/* Header with cancel button */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-4">
                {showBackButton && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goBack}
                    className="gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Button>
                )}
                <h1 className="text-2xl font-bold text-primary">New Visit</h1>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="gap-2 text-muted-foreground hover:text-red-600"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
            </div>

            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between items-center text-sm text-muted-foreground mb-2">
                <span className="font-medium">
                  Step {currentStepNumber} of {totalSteps}: {STEP_LABELS[state.currentStep]}
                </span>
                <span className="font-medium">{Math.round(progress)}% Complete</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Step Content */}
            <div className="min-h-[500px]">
              {renderStep()}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

// Main wizard component with provider
export default function VisitWizard() {
  return (
    <VisitWizardProvider>
      <WizardContent />
    </VisitWizardProvider>
  )
}
